/**
 * PAP Sync Hook
 *
 * Manages the full PAP sync lifecycle: trigger, poll for real-time progress, show results.
 * Polls org settings every 3s after triggering a sync to read `papSync.progress`
 * and detect completion via `papSync.status === 'completed'`.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

const POLL_INTERVAL_MS = 3_000;

export type PapSyncProgress = {
  pagesProcessed: number;
  productsProcessed: number;
  skusUpserted: number;
};

export type PapSyncData = {
  status?: 'running' | 'completed' | 'failed';
  startedAt?: string;
  lastCompletedAt?: string;
  progress?: PapSyncProgress;
  error?: string | null;
  source?: string;
  productType?: string | null;
  // Legacy fields (pre-progress tracking)
  productsProcessed?: number;
  skusUpserted?: number;
};

type OrgSettingsWithPapSync = {
  papSync?: PapSyncData;
};

export function usePapSync(options?: { onComplete?: () => void }) {
  const { t } = useTranslation('settings');
  const { organization, reloadOrganization } = useOrganization();
  const [isSyncing, setIsSyncing] = useState(false);
  const syncClickedAtRef = useRef<number>(0);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const onCompleteRef = useRef(options?.onComplete);
  onCompleteRef.current = options?.onComplete;

  const mutation = trpc.organization.runPapProductSync.useMutation();

  const papSync = (organization?.settings as OrgSettingsWithPapSync | null)?.papSync ?? undefined;

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = undefined;
  }, []);

  // Watch org settings for sync completion or failure
  useEffect(() => {
    if (!isSyncing || !syncClickedAtRef.current) return;
    if (!papSync?.status) return;

    if (papSync.status === 'completed') {
      const completedAt = papSync.lastCompletedAt ? new Date(papSync.lastCompletedAt).getTime() : 0;
      if (completedAt > syncClickedAtRef.current) {
        setIsSyncing(false);
        stopPolling();

        const p = papSync.progress;
        const parts: string[] = [];
        if (p?.productsProcessed != null) parts.push(`${p.productsProcessed} products`);
        if (p?.skusUpserted != null) parts.push(`${p.skusUpserted} SKUs upserted`);

        toast({
          title: t('toast.papSyncComplete'),
          description: parts.length > 0 ? parts.join(', ') : t('toast.papSyncComplete'),
        });

        onCompleteRef.current?.();
      }
    } else if (papSync.status === 'failed') {
      const startedAt = papSync.startedAt ? new Date(papSync.startedAt).getTime() : 0;
      if (startedAt >= syncClickedAtRef.current) {
        setIsSyncing(false);
        stopPolling();

        toast({
          title: t('toast.papSyncFailed'),
          description: papSync.error || t('toast.papSyncFailed'),
          variant: 'destructive',
        });
      }
    }
  }, [isSyncing, papSync?.status, papSync?.lastCompletedAt, papSync?.startedAt, stopPolling]);

  // Re-attach to an in-progress sync on mount (e.g. user navigated away and came back)
  useEffect(() => {
    if (!isSyncing && papSync?.status === 'running' && papSync.startedAt) {
      syncClickedAtRef.current = new Date(papSync.startedAt).getTime();
      setIsSyncing(true);
      pollIntervalRef.current = setInterval(() => {
        reloadOrganization();
      }, POLL_INTERVAL_MS);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // Cleanup on unmount
  useEffect(() => stopPolling, [stopPolling]);

  const startSync = useCallback((productType?: string, syncRange?: 'full' | 'since_last' | '7d' | '30d' | '90d') => {
    if (isSyncing) return;

    syncClickedAtRef.current = Date.now();
    setIsSyncing(true);

    const input: { productType?: string; syncRange?: 'full' | 'since_last' | '7d' | '30d' | '90d' } = {};
    if (productType) input.productType = productType;
    if (syncRange) input.syncRange = syncRange;

    mutation.mutate(
      input,
      {
        onSuccess: () => {
          // Poll for progress updates — no timeout, sync completes when it completes
          pollIntervalRef.current = setInterval(() => {
            reloadOrganization();
          }, POLL_INTERVAL_MS);
        },
        onError: (err) => {
          setIsSyncing(false);
          toast({
            title: t('toast.papSyncFailed'),
            description:
              err instanceof Error ? err.message : t('toast.failedPapSync'),
            variant: 'destructive',
          });
        },
      }
    );
  }, [isSyncing, mutation, reloadOrganization, stopPolling]);

  return { startSync, isSyncing, papSync };
}
